import {
  memo,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useAtomValue } from "jotai";
import { Dialog } from "radix-ui";
import {
  Archive,
  Bell,
  Check,
  ExternalLink,
  Mail,
  RefreshCw,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  guestNotificationFeedAtom,
  notificationUserIdAtom,
} from "@/src/atoms/studioNotifications";
import type {
  GuestNotificationFeed,
  GuestNotice,
} from "@/src/services/guestNotificationFeed";
import type {
  NotificationAction,
  StudioNotification,
} from "@/src/services/studioNotifications";
import { useStudioNotifications } from "./useStudioNotifications";
import "./StudioNotifications.css";

type Notice = GuestNotice | StudioNotification;

const CHANGELOG_URL = "https://eriol.io/changelog";

/**
 * Abre un link en el navegador del sistema: en Desktop, vía el bridge (un
 * `target="_blank"` abriría una ventana de Electron); en web, una pestaña.
 */
function openExternal(url: string) {
  if (window.electronAPI?.openExternal) {
    void window.electronAPI.openExternal(url);
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function NotificationSession({ feed }: { feed: GuestNotificationFeed }) {
  const { t, i18n } = useTranslation("notifications");
  const navigate = useNavigate();
  const remote = useStudioNotifications();
  const guests = useSyncExternalStore(feed.subscribe, feed.snapshot);
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  // Distancia al borde derecho para que el panel cuelgue de la campana (a su
  // derecha quedan el perfil y los botones de la ventana), acotada para que
  // el panel entre en el viewport.
  const [anchorRight, setAnchorRight] = useState<number | null>(null);
  const items: Notice[] = [...guests, ...remote.items].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  const unread = items.filter(
    (item) => !item.readAt && !item.archivedAt,
  ).length;
  const pending = guests.filter((item) => item.status === "pending").length;
  // Una sola lista (sin pestañas por tipo: cada fila ya dice su categoría);
  // archivar saca la notificación de la bandeja.
  const visible = items.filter((item) => !item.archivedAt);
  const anchorToBell = () => {
    const rect = bellRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const panelWidth = Math.min(360, window.innerWidth - 20);
    const fromBell = Math.round(window.innerWidth - rect.right);
    const maxRight = Math.max(10, window.innerWidth - panelWidth - 10);
    setAnchorRight(Math.min(Math.max(10, fromBell), maxRight));
  };
  // La ventana se puede redimensionar con el panel abierto: se re-ancla.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", anchorToBell);
    return () => window.removeEventListener("resize", anchorToBell);
  }, [open]);
  const changeOpen = (value: boolean) => {
    setOpen(value);
    if (value) {
      anchorToBell();
      void remote.refresh(true);
    }
  };
  const update = async (selected: Notice[], action: NotificationAction) => {
    const server = selected
      .filter((item) => item.category !== "guests")
      .map((item) => item.id);
    if (server.length && !(await remote.update(server, action))) return;
    feed.update(
      selected
        .filter((item) => item.category === "guests")
        .map((item) => item.id),
      action,
    );
  };
  const title = (item: Notice) =>
    item.category === "guests"
      ? item.name
      : item.kind === "release"
        ? item.values.title
        : t(`kind.${item.kind}`);
  const details = (item: Notice) =>
    item.category === "guests"
      ? t(`guest.${item.status}`, {
          time: new Intl.DateTimeFormat(i18n.language, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }).format(new Date(item.expiresAt)),
        })
      : item.kind === "release"
        ? t("version", { version: item.values.version })
        : t(`detail.${item.kind}`, item.values);

  return (
    <Dialog.Root open={open} onOpenChange={changeOpen} modal={false}>
      <Dialog.Trigger asChild>
        <button
          ref={bellRef}
          type="button"
          className="titlebar-nodrag studio-notifications-bell"
          title={t("title")}
          aria-label={t("bell", { count: unread })}
        >
          <Bell size={17} aria-hidden="true" />
          {unread > 0 && (
            <span className={pending ? "has-guests" : ""} aria-hidden="true">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </Dialog.Trigger>
      {!open && pending > 0 && (
        <div className="studio-guest-notice" role="status">
          <button
            type="button"
            onClick={() => changeOpen(true)}
          >
            <UsersRound size={18} aria-hidden="true" />
            {t("pending", { count: pending })}
          </button>
        </div>
      )}
      <Dialog.Portal>
        <Dialog.Content
          className="studio-notifications"
          style={anchorRight === null ? undefined : { right: anchorRight }}
          aria-describedby={undefined}
        >
          <header>
            <Dialog.Title>{t("title")}</Dialog.Title>
            <button
              type="button"
              title={t("refresh")}
              aria-label={t("refresh")}
              disabled={remote.loading}
              onClick={() => void remote.refresh(true)}
            >
              <RefreshCw size={17} />
            </button>
            <Dialog.Close title={t("close")} aria-label={t("close")}>
              <X size={18} />
            </Dialog.Close>
          </header>
          {remote.loading && (
            <p className="studio-notifications-status" role="status">
              {t("loading")}
            </p>
          )}
          {remote.error && (
            <div className="studio-notifications-error" role="alert">
              {t("error")}
              <button
                type="button"
                disabled={remote.loading}
                onClick={() => void remote.refresh(true)}
              >
                {t("retry")}
              </button>
            </div>
          )}
          <div className="studio-notifications-list">
            {!visible.length && !remote.loading && (
              <p className="studio-notifications-empty">
                {t(remote.error ? "unavailable" : "empty")}
              </p>
            )}
            <ul>
              {visible.map((item) => (
                <li
                  key={`${item.category}:${item.id}`}
                  className={!item.readAt ? "is-unread" : ""}
                >
                  <div className={`studio-notification-icon ${item.category}`}>
                    {item.category === "guests" ? (
                      <UsersRound size={16} />
                    ) : item.category === "account" ? (
                      <UserRound size={16} />
                    ) : (
                      <Bell size={16} />
                    )}
                  </div>
                  <div className="studio-notification-body">
                    <div className="studio-notification-meta">
                      <span>{t(`category.${item.category}`)}</span>
                      <time dateTime={item.createdAt}>
                        {new Intl.DateTimeFormat(i18n.language, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(item.createdAt))}
                      </time>
                    </div>
                    <h3>{title(item)}</h3>
                    <p>{details(item)}</p>
                    {item.category === "guests" &&
                      item.status === "pending" && (
                        <div className="studio-notification-commands">
                          <button
                            type="button"
                            className="primary"
                            onClick={() => void feed.respond(item.id, true)}
                          >
                            <Check size={15} />
                            {t("accept")}
                          </button>
                          <button
                            type="button"
                            onClick={() => void feed.respond(item.id, false)}
                          >
                            <X size={15} />
                            {t("reject")}
                          </button>
                        </div>
                      )}
                    {item.category === "account" && (
                      <button
                        type="button"
                        className="studio-notification-link"
                        onClick={() => {
                          void update([item], "read");
                          setOpen(false);
                          navigate(
                            item.kind === "profile"
                              ? "/install"
                              : "/settings?tab=integrations",
                          );
                        }}
                      >
                        {t(
                          item.kind === "profile"
                            ? "review"
                            : "reviewIntegration",
                        )}
                      </button>
                    )}
                    {item.category === "news" && (
                      <button
                        type="button"
                        className="studio-notification-link"
                        onClick={() => {
                          void update([item], "read");
                          openExternal(CHANGELOG_URL);
                        }}
                      >
                        {t("readRelease")}
                        <ExternalLink size={12} />
                      </button>
                    )}
                    <div className="studio-notification-actions">
                      <button
                        type="button"
                        title={t(item.readAt ? "unread" : "read")}
                        aria-label={t(item.readAt ? "unread" : "read")}
                        disabled={remote.loading}
                        onClick={() =>
                          void update([item], item.readAt ? "unread" : "read")
                        }
                      >
                        {item.readAt ? <Mail size={14} /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        title={t("archive")}
                        aria-label={t("archive")}
                        disabled={
                          remote.loading ||
                          (item.category === "guests" &&
                            (item.status === "pending" ||
                              item.status === "connecting"))
                        }
                        onClick={() => void update([item], "archive")}
                      >
                        <Archive size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function StudioNotifications() {
  const userId = useAtomValue(notificationUserIdAtom);
  const feed = useAtomValue(guestNotificationFeedAtom);
  return userId && feed ? (
    <NotificationSession key={userId} feed={feed} />
  ) : null;
}
export default memo(StudioNotifications);
